import { useMemo } from 'react';
import {
  IssueMessageType,
  SortDirectionEnum,
  useFindByQuery,
} from '../../graphql';

export type SideChatSummary = {
  exists: true;
  replyCount: number;
  /**
   * Who started the side chat — the author of its `SYSTEM_MESSAGE` divider,
   * which is written once by `useStartSideChat`. Names the pill on a feed row,
   * where one row can surface several side chats and a bare count would not
   * say which is which. Undefined for a side chat whose divider is outside the
   * page slice below.
   */
  starterName?: string;
};

/** Minimal shape needed to build side-chat summaries. */
export type SideChatSummarySource = {
  id: string;
  type: IssueMessageType;
  parentMessage?: { id: string } | null;
  createdBy?: { name?: { first?: string; last?: string } } | null;
};

/**
 * Side Chat (plan 0022 §B): group a flat list of a single issue's messages into
 * `Map<parentMessageId, { exists, replyCount }>`.
 *
 * - existence = ANY message parented to that origin (so the pill shows even at 0
 *   replies, i.e. only the SYSTEM_MESSAGE divider exists yet).
 * - replyCount = count of parented USER_MESSAGE only — the SYSTEM_MESSAGE
 *   divider ("Side chat started from…") is deliberately excluded.
 *
 * Grouped client-side (the blessed pattern) rather than via a server null
 * filter — see useFindIssueMessages for why the null relationship filter is
 * unsafe.
 */
export const groupSideChatSummaries = (
  messages: SideChatSummarySource[]
): Map<string, SideChatSummary> => {
  const map = new Map<string, SideChatSummary>();

  for (const message of messages) {
    const parentId = message.parentMessage?.id;
    if (!parentId) continue;

    const summary = map.get(parentId) ?? { exists: true as const, replyCount: 0 };
    if (message.type === IssueMessageType.UserMessage) {
      summary.replyCount += 1;
    }
    if (message.type === IssueMessageType.SystemMessage) {
      const name = [message.createdBy?.name?.first, message.createdBy?.name?.last]
        .filter(Boolean)
        .join(' ');
      if (name) summary.starterName = name;
    }
    map.set(parentId, summary);
  }

  return map;
};

// KNOWN LIMITATION: this caps at 1000 messages with no pagination — an issue
// with more than 1000 messages will silently drop whatever falls outside this
// slice from the reply-count pills. The `orderBy` below (matching the sibling
// useFindIssueMessages/useSideChatMessages convention) at least makes the
// slice deterministic — oldest 1000 by `updatedAt` — rather than whatever
// order Mongo happens to return. Full pagination/aggregation is out of scope
// here.
const SIDE_CHAT_SUMMARY_PAGE_SIZE = 1000;

/**
 * One lightweight `findIssueMessages` for the whole issue (selecting only
 * `{ id, type, parentMessage { id } }`), grouped client-side into per-origin
 * summaries that drive the "X replies · Side Chat" pills.
 *
 * `enabled` defaults to `true` for backward compatibility, but callers behind
 * the Side Chat feature flag should pass `enabled: isSideChatEnabled` — this
 * query otherwise fires (a 1000-row `findIssueMessages`) on every activity
 * open regardless of the flag.
 */
export const useSideChatSummaries = (
  issueId: string,
  options?: { enabled?: boolean }
) => {
  const { data, isLoading, refetch, invalidateQuery } = useFindByQuery({
    modelName: 'issueMessage',
    query: FIND_SIDE_CHAT_SUMMARIES,
    variables: {
      filter: { issue: { eq: issueId } },
      page: { limit: SIDE_CHAT_SUMMARY_PAGE_SIZE, offset: 0 },
      orderBy: { field: 'updatedAt', order: SortDirectionEnum.Asc },
    },
    options: { enabled: options?.enabled ?? true },
  });

  const summaries = useMemo(
    () => groupSideChatSummaries((data?.items ?? []) as SideChatSummarySource[]),
    [data]
  );

  return { summaries, isLoading, refetch, invalidateQuery };
};

const FIND_SIDE_CHAT_SUMMARIES = `
  query findIssueMessages($filter: IssueMessageFilter, $page: PageRequest, $orderBy: OrderByInput) {
    findIssueMessages(filter: $filter, page: $page, orderBy: $orderBy) {
      items {
        id
        type
        parentMessage {
          id
        }
        createdBy {
          name {
            first
            last
          }
        }
      }
      count
      offset
      limit
    }
  }
`;
